import React, { useState, useEffect } from 'react';
import { format, subDays, addDays, isSameDay } from 'date-fns';
import { useAuth } from '../context/AuthContext';
import { useDate } from '../pages/Layout';
import GlassCard from '../../../shared/components/card/GlassCard';
import DotGrid from './DotGrid'; // Ajusta la ruta según tu estructura

const ProductosTable = ({showTopVentas = true,showTopRecepcionados = true}) => {
  const { getTopproductos } = useAuth();
  
  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { currentDate, setIsLoading } = useDate();

  useEffect(() => {
    const fetchProductos = async () => {
      try {
        setLoading(true);
        setError(null);
        const date = format(currentDate, 'yyyy-MM-dd');
        const productosPetition = await getTopproductos(date);
        const data = productosPetition.data; 
        
        console.log("data productos: ", data);
        
        if (!data || !Array.isArray(data)) {
          throw new Error('Formato de datos incorrecto');
        }
        setProductos(data);
      } catch (err) {
        console.error('Error cargando productos:', err);
        setError(err.message || 'Error al cargar los productos');
      } finally {
        setLoading(false);
      }
    };

    fetchProductos();
  }, [getTopproductos, currentDate]);

  // Top 10 por ventas
  const topVentas = [...productos]
    .filter(p => (p.unidadesVendidas || 0) > 0)
    .sort((a, b) => (b.unidadesVendidas || 0) - (a.unidadesVendidas || 0))
    .slice(0, 10);

  // Top 10 por compras
  const topCompras = [...productos]
    .filter(p => (p.unidadesCompradas || 0) > 0)
    .sort((a, b) => (b.unidadesCompradas || 0) - (a.unidadesCompradas || 0))
    .slice(0, 10);

  // Componente reutilizable para renderizar una tabla de productos
  const renderProductoTable = (productosOrdenados, titulo, subtitulo, tipo) => (
    <div className="productos-column">
      <div className="productos-header">
        <div>
          <h2 className="productos-title">{titulo}</h2>
          <div className="productos-subtitle">{subtitulo}</div>
        </div>
        <div className="productos-badge">
          {productosOrdenados.length} productos
        </div>
      </div>

      <div className="productos-table">
        {productosOrdenados.map((producto, index) => (
          <div 
            key={producto.codigo} 
            className={`producto-row producto-row--rank-${index + 1}`}
          >
            <div className={`producto-rank producto-rank--${index + 1}`}>
              {index + 1}
            </div>

            <div className="producto-info">
              <div className={`producto-nombre producto-nombre--${index + 1}`}>
                {producto.nombre}
              </div>
              <div className={`producto-codigo producto-codigo--${index + 1}`}>
                {producto.codigo}
              </div>
            </div>

            {tipo === 'ventas' && (
              <div className="producto-metric producto-metric--ventas">
                <span className={`metric-label metric-label--${index + 1}`}>Vendidas</span>
                <span className={`metric-value metric-value--ventas metric-value--${index + 1}`}>
                  {producto.unidadesVendidas?.toLocaleString() || 0}
                </span>
              </div>
            )}

            {tipo === 'compras' && (
              <div className="producto-metric producto-metric--compras">
                <span className={`metric-label metric-label--${index + 1}`}>Recepcionadas</span>
                <span className={`metric-value metric-value--compras metric-value--${index + 1}`}>
                  {producto.unidadesCompradas?.toLocaleString() || 0}
                </span>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  // Componente reutilizable para skeleton loading
  const renderSkeletonTable = (titulo, tipo) => (
    <div className="productos-column">
      <div className="productos-header">
        <h2 className="productos-title">{titulo}</h2>
      </div>
      
      <div className="stats-content frosted-pulse">
        <div className="loading-overlay">
          <DotGrid />
        </div>
        
        <div className="blurred-content">
          <div className="productos-table">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className={`producto-row producto-row--rank-${i}`}>
                <div className={`producto-rank producto-rank--${i}`}>{i}</div>
                <div className="producto-info">
                  <div className={`producto-nombre producto-nombre--${i}`}>Producto {i}</div>
                  <div className={`producto-codigo producto-codigo--${i}`}>COD{i}123</div>
                </div>
                {tipo === 'ventas' && (
                  <div className="producto-metric producto-metric--ventas">
                    <span className={`metric-label metric-label--${i}`}>Vendidas</span>
                    <span className={`metric-value metric-value--ventas metric-value--${i}`}>1,234</span>
                  </div>
                )}
                {tipo === 'compras' && (
                  <div className="producto-metric producto-metric--compras">
                    <span className={`metric-label metric-label--${i}`}>Compradas</span>
                    <span className={`metric-value metric-value--compras metric-value--${i}`}>567</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  // ========== ESTADO DE CARGA CON FROSTED PULSE ==========
  if (loading) {
    return (
      <div className="productos-container">
        <GlassCard maxWidth='100%'>
          <div className="productos-dual-layout">
            {renderSkeletonTable("Top Vendidos", "ventas")}
            {renderSkeletonTable("Top Comprados", "compras")}
          </div>
        </GlassCard>
      </div>
    );
  }

  // ========== ESTADO DE ERROR ==========
  if (error) {
    return (
      <div className="productos-container">
        <GlassCard maxWidth='100%'>
          <div className="productos-header">
            <h2 className="productos-title">Top Productos</h2>
          </div>
          <div className="productos-error">
            <span className="error-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
            </span>
            <span className="error-text">No se pudieron cargar los datos de los Productos</span>
            <button 
              className="retry-btn"
              onClick={() => window.location.reload()}
            >
              Reintentar
            </button>
          </div>
        </GlassCard>
      </div>
    );
  }

  // ========== ESTADO VACÍO ==========
  if (productos.length === 0) {
    return (
      <div className="productos-container">
        <GlassCard maxWidth='100%'>
          <div className="productos-header">
            <h2 className="productos-title">Top Productos</h2>
          </div>
          <div className="productos-empty">
            <span className="productos-empty-icon">📦</span>
            <span>No hay datos de productos disponibles</span>
            <span className="empty-subtext">Intenta con otra fecha</span>
          </div>
        </GlassCard>
      </div>
    );
  }

  // ========== ESTADO CON DATOS ==========
  return (
    <div className="productos-container">
      <GlassCard maxWidth='100%'>
        <div className="productos-dual-layout">
          {showTopVentas && renderProductoTable(topVentas, "Top Vendidos", "Ranking por unidades vendidas", "ventas")}
          {showTopRecepcionados && renderProductoTable(topCompras, "Top Recepcionados", "Ranking por unidades recepcionadas", "compras")}
        </div>
      </GlassCard>
    </div>
  );
};

export default ProductosTable;